import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { Dialog, DialogType, DialogFooter, PrimaryButton, Stack, Text, FontIcon, DefaultButton, ButtonType, TextField, Checkbox, mergeStyleSets } from '@fluentui/react';
import { NeutralColors, SharedColors } from '@fluentui/theme';
import { useBoolean } from '@fluentui/react-hooks';
import { ExternalLink } from '@shared/components/externalLink';
import { ratingValues } from '@shared/Models';
import { useTelemetry } from '@shared/hooks/useTelemetry';
import { Constants } from '@shared/utils/constants';
const termsUrl = 'https://azure.microsoft.com/support/legal/marketplace-terms';
const privacyUrl = 'https://privacy.microsoft.com/en-us/privacystatement';
const modalMinWidth = '560px';
const modalProps = {
    isDarkOverlay: false,
    isBlocking: true
};
const stackTokens = {
    childrenGap: 16
};
const classNames = mergeStyleSets({
    externalLink: [
        {
            display: 'inline-block',
            color: `${SharedColors.cyanBlue10} !important`
        }
    ],
    defaultText: [
        {
            color: NeutralColors.gray130
        }
    ],
    divider: [
        {
            fontSize: 16,
            color: NeutralColors.gray160,
            lineHeight: 20
        }
    ]
});
const dialogContentStyles = {
    header: {
        color: NeutralColors.gray160
    },
    subText: {
        color: NeutralColors.gray160,
        fontWeight: 600
    }
};
const dialogTextFieldStyles = {
    fieldGroup: {
        height: 96
    }
};
const buttonStyles = {
    root: {
        width: 40,
        height: 48,
        minWidth: 0,
        border: '1px solid #E1E3E5'
    },
    rootChecked: {
        color: NeutralColors.white,
        backgroundColor: SharedColors.cyanBlue10
    },
    rootCheckedHovered: {
        color: NeutralColors.white,
        backgroundColor: SharedColors.cyanBlue10
    }
};
const dialogContentProps = (context)=>{
    return {
        type: DialogType.close,
        title: context.loc('Nps_SurveyModal_Title', 'We’d love your feedback'),
        subText: context.loc('Nps_SurveyModal_SubHeader_Text', 'How likely are you to recommend AppSource to a friend or colleague?'),
        closeButtonAriaLabel: context.loc('Nps_SurveyModal_CloseButton_AriaText', 'Close'),
        styles: dialogContentStyles
    };
};
const checkboxLabelDefaultText = (email)=>`It’s OK to contact me at ${email} about my feedback`;
const additionalInfoDefaultText = 'For specific concerns or issues regarding the use of an application, please contact the publisher via the support link on the application product page.';
const NpsTermsAndPrivacy = ({ context })=>{
    return /*#__PURE__*/ _jsxs(Stack, {
        horizontal: true,
        children: [
            /*#__PURE__*/ _jsx(ExternalLink, {
                className: classNames.externalLink,
                accessibilityEnabled: true,
                href: termsUrl,
                target: "_blank",
                rel: "noreferrer",
                children: context.loc('Nps_SurveyModal_Terms_Link_Text', 'Terms')
            }),
            /*#__PURE__*/ _jsx(FontIcon, {
                iconName: "Separator",
                className: classNames.divider
            }),
            /*#__PURE__*/ _jsx(ExternalLink, {
                className: classNames.externalLink,
                accessibilityEnabled: true,
                href: privacyUrl,
                target: "_blank",
                rel: "noreferrer",
                children: context.loc('Nps_SurveyModal_PrivacyStatement_Link_Text', 'Privacy statement')
            })
        ]
    });
};
const NpsDescription = ({ value, onChange, context })=>{
    return /*#__PURE__*/ _jsx(TextField, {
        label: context.loc('Nps_SurveyModal_Description_Label_Text', 'What can we do to improve your experience?'),
        multiline: true,
        resizable: false,
        placeholder: context.loc('Nps_SurveyModal_Description_Placeholder_Text', 'Tell us what you think of AppSource.'),
        value: value,
        onChange: (_, newValue)=>onChange(newValue || ''),
        styles: dialogTextFieldStyles
    });
};
const NpsRatings = ({ selectedRating, onChange, context })=>{
    return /*#__PURE__*/ _jsxs(Stack, {
        tokens: {
            childrenGap: 8
        },
        children: [
            /*#__PURE__*/ _jsxs(Stack, {
                horizontal: true,
                horizontalAlign: "space-between",
                children: [
                    /*#__PURE__*/ _jsx(Text, {
                        variant: "smallPlus",
                        className: classNames.defaultText,
                        children: context.loc('Nps_SurveyModal_Ratings_Unsatisfied_Text', 'Not at all likely')
                    }),
                    /*#__PURE__*/ _jsx(Text, {
                        variant: "smallPlus",
                        className: classNames.defaultText,
                        children: context.loc('Nps_SurveyModal_Ratings_Satisfied_Text', 'Extremely likely')
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Stack, {
                horizontal: true,
                tokens: {
                    childrenGap: 8
                },
                children: ratingValues.map((ratingValue)=>/*#__PURE__*/ _jsx(DefaultButton, {
                        toggle: true,
                        buttonType: selectedRating === ratingValue ? ButtonType.primary : ButtonType.default,
                        checked: selectedRating === ratingValue,
                        styles: buttonStyles,
                        text: `${ratingValue}`,
                        onClick: ()=>onChange(ratingValue)
                    }, ratingValue))
            })
        ]
    });
};
export const NpsSurveyModal = ({ hidden, onDismiss, onSubmit, context, email = '' })=>{
    const [contactCheckboxChecked, { toggle: toggleContactCheckbox }] = useBoolean(false);
    const [selectedRating, setSelectedRating] = useState(null);
    const [description, setDescription] = useState('');
    const [{ pageAction }] = useTelemetry();
    const handleSubmit = ()=>{
        onSubmit({
            description,
            email: contactCheckboxChecked && email ? email : '',
            rating: selectedRating
        });
        pageAction(null, {
            behavior: 142,
            actionType: 'CL',
            contentTags: {
                id: Constants.Telemetry.Id.Nps,
                name: Constants.Telemetry.FeedbackName.Nps,
                fbnm: Constants.Telemetry.FeedbackName.Nps,
                vtbm: description,
                rate: `${selectedRating}`,
                sat: '0-10'
            }
        });
    };
    return /*#__PURE__*/ _jsxs(Dialog, {
        hidden: hidden,
        dialogContentProps: dialogContentProps(context),
        minWidth: modalMinWidth,
        modalProps: modalProps,
        onDismiss: onDismiss,
        children: [
            /*#__PURE__*/ _jsxs(Stack, {
                tokens: stackTokens,
                children: [
                    /*#__PURE__*/ _jsx(NpsRatings, {
                        selectedRating: selectedRating,
                        onChange: setSelectedRating,
                        context: context
                    }),
                    /*#__PURE__*/ _jsx(NpsDescription, {
                        value: description,
                        onChange: setDescription,
                        context: context
                    }),
                    /*#__PURE__*/ _jsx(Text, {
                        children: context.loc('Nps_SurveyModal_AdditionalInfo_Text', additionalInfoDefaultText)
                    }),
                    email && /*#__PURE__*/ _jsx(Checkbox, {
                        label: context.locParams('Nps_SurveyModal_CheckboxLabel_Text', [
                            email
                        ], checkboxLabelDefaultText(email)),
                        checked: contactCheckboxChecked,
                        onChange: toggleContactCheckbox
                    }),
                    /*#__PURE__*/ _jsx(NpsTermsAndPrivacy, {
                        context: context
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(DialogFooter, {
                children: /*#__PURE__*/ _jsx(PrimaryButton, {
                    text: context.loc('Nps_SurveyModal_SubmitButton_Text', 'Submit'),
                    onClick: handleSubmit,
                    disabled: selectedRating === null
                })
            })
        ]
    });
};
